import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  RadioGroup,
  Radio,
  Spinner,
  Select,
  useColorModeValue,
  ScaleFade, // Chakra UI for animation
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useParams } from "react-router-dom";
import { ENDPOINTS } from "utils/Constants";
import { currentUser } from "utils/CurrentUser";
import { notify } from "components/ToastNotification";
import Banner from "./components/Banner";
import { USER_LEVELS } from "utils/Constants";

export default function SkillAssessment() {
  const { skillId: urlSkillId } = useParams();
  const [skills, setSkills] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSkillId, setSelectedSkillId] = useState(urlSkillId || null);
  const [currentLevel, setCurrentLevel] = useState(USER_LEVELS.includes(searchParams.get('currentLevel'))
    ? searchParams.get('currentLevel').toLocaleLowerCase()
    : USER_LEVELS[0].toLocaleLowerCase());
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [answerStatus, setAnswerStatus] = useState({});
  const [quiz, setQuiz] = useState({});
  const [answers, setAnswers] = useState({});
  const [errorState, setErrorState] = useState({});
  const [loading, setLoading] = useState(false);

  const [results, setResults] = useState(null); 
  const [allCorrect, setAllCorrect] = useState(false);

  const bgColor = useColorModeValue("gray.100", "gray.700");
  const cardColor = useColorModeValue("white", "gray.800");
  // const borderColor = useColorModeValue("red", "red");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const cardBg = useColorModeValue("gray.50", "gray.700");

  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axios.get(ENDPOINTS.GET_MY_LEARNING_SKILLS, {
          headers: { Authorization: `Bearer ${currentUser.token}` },
        });
        setSkills(response.data.data);
      } catch (error) {
        notify("Unable to fetch skills", "error");
      }
    };
    fetchSkills();
  }, []);

  useEffect(() => {
    if (selectedSkillId) {
      const fetchQuestions = async () => {
        setLoading(true);
        try {
          const response = await axios.get(
            `${ENDPOINTS.GET_SKILL_ASSESSMENT}${selectedSkillId}?userLevel=${currentLevel}`,
            {
              headers: { Authorization: `Bearer ${currentUser.token}` },
            }
          );
          setAssessmentQuestions(response.data.data.quiz || []);
          setQuiz(response.data.data);
          setAnswers({});
          setErrorState({});
          setResults(null);
          setAllCorrect(false);
        } catch (error) {
          notify("Unable to fetch skill details.", "error");
        } finally {
          setLoading(false);
        }
      };
      fetchQuestions();
    }
  }, [selectedSkillId, currentLevel]);

  const handleAnswerChange = (questionIndex, answerIndex) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: answerIndex }));
    setErrorState((prev) => ({ ...prev, [questionIndex]: false }));
  };

  const handleSubmit = async () => {
    const newErrorState = {};
    const answerStatusObj = {};
    let hasError = false;
    setAnswerStatus({});
    assessmentQuestions.forEach((question, index) => {
      if (!answers[index]) {
        newErrorState[index] = true;
        hasError = true;
      }
    });

    setErrorState(newErrorState);

    if (hasError) {
      notify("Please answer all the questions.", "error");
      return;
    }

    try {
      const formattedAnswers = assessmentQuestions.map((question, index) => {
        const selectedAnswer = answers[index];
        const isCorrectOrNot = question.answers.filter(
          (answer) => answer.text === selectedAnswer
        );

        return {
          questionId: question.id,
          selectedAnswer: selectedAnswer,
          isCorrect: isCorrectOrNot ? isCorrectOrNot[0]?.isCorrect : false,
        };
      });

      const response = await axios.post(
        ENDPOINTS.SUBMIT_ASSESSMENT,
        {
          skillId: selectedSkillId,
          quizId: quiz?.id,
          answers: formattedAnswers,
        },
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      );

      const { quizScore, percentage, quizCompleted, allAnswers } = response.data.data;

      notify(response.data.output, response.data.success ? "success" : "error");

      setResults({ quizScore, percentage, quizCompleted, allAnswers });

      const incorrectAnswers = allAnswers || [];
      incorrectAnswers.forEach(({ questionId, isCorrect }) => {
        const questionIndex = assessmentQuestions.findIndex(
          (question) => question.id === questionId
        );
        if (questionIndex !== -1) {
          answerStatusObj[questionIndex] = isCorrect ? "correct" : "incorrect";
        }
      });
      setAnswerStatus(answerStatusObj);

      // Check if all answers are correct
      const allAnswersCorrect = formattedAnswers.every((answer) => answer.isCorrect);
      setAllCorrect(allAnswersCorrect);
    } catch (error) {
      notify("Failed to submit assessment.", "error");
    }
  };

  const handleOnChangeSelectedSkill = (target) => {
    const selectedSkillId = target.options[target.selectedIndex].getAttribute("data-value");
    const currentLevel = target.options[target.selectedIndex].getAttribute("data-current-level");
    
    setSelectedSkillId(selectedSkillId);
    setCurrentLevel(currentLevel);
  }

  return (
    <Box pt={{ base: "180px", md: "80px", xl: "80px" }} bg={cardBg} p="20px" borderRadius="10px" boxShadow="md" mb="20px">
      {/* Skill Selection */}
      <Banner />
      {!results?.quizCompleted && (
        <Flex mb="4" mt={"4"}>
          <Select
            placeholder="Select a skill"
            value={(selectedSkillId && currentLevel) ? selectedSkillId + '-' + currentLevel : ""}
            onChange={(e) => handleOnChangeSelectedSkill(e.target)}
            bg={cardColor}
            color={textColor}
          >
            {skills.map((skill) => (
              <option key={skill.id + skill.currentLevel} value={skill.id + '-' + skill.currentLevel} data-value={skill.id} data-current-level={skill.currentLevel}>
                {skill.name} - ({skill.currentLevel.charAt(0).toUpperCase() + skill.currentLevel.slice(1)})
              </option>
            ))}
          </Select>
        </Flex>
      )}

      {/* Loading Indicator */}
      {loading && (
        <Flex justifyContent="center" alignItems="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      )}

      {/* Assessment Questions */}
      {!loading && selectedSkillId && assessmentQuestions.length > 0 && !results?.quizCompleted && (
        <VStack spacing="4" align="stretch">
          {assessmentQuestions.map((question, index) => {
            const status = answerStatus[index];
            let borderColor = (errorState[index] ? "red" : "none");
            if (status) {
              borderColor =
                (status === "incorrect"
                  ? "red"
                  : status === "correct"
                    ? "green"
                    : "green");
            }

            return (
              <Box
                key={index}
                p="4"
                bg={bgColor}
                borderRadius="md"
                border={borderColor ? `2px solid ${borderColor}` : "none"} // Conditional border based on status
              >
                <Text mb="2" fontWeight="bold">
                  {index + 1}. {question.question}
                </Text>
                <RadioGroup
                  value={answers[index] || ""}
                  onChange={(value) => handleAnswerChange(index, value)}
                >
                  {question.answers.map((answer, answerIndex) => (
                    <>
                      <Radio key={answerIndex} value={answer.text} colorScheme="blue">
                        {answer.text}
                      </Radio>
                      <br />
                    </>
                  ))}
                </RadioGroup>
              </Box>
            );
          })}
          <Button colorScheme="blue" onClick={handleSubmit}>
            Submit Assessment
          </Button>
        </VStack>
      )}

      {/* Results Display */}
      {results && (
        <ScaleFade in={true} initialScale={0.9} bg="green.100">
          <Box
            mt="6"
            p="6"
            bg="yellow.500"
            borderRadius="md"
            textAlign="center"
            boxShadow="lg"
          >
            <Text fontSize="2xl" fontWeight="bold" color={"black"}>
              Results
            </Text>
            <Text mt="2" color={"black"}>Score: {results.quizScore}</Text>
            <Text color={"black"}>Percentage: {results.percentage}%</Text>
            <Text color={"black"}>
              Quiz Completed: {results.quizCompleted ? "Yes" : "No"}
            </Text>
          </Box>
        </ScaleFade>
      )}

      {/* All Correct Animation */}
      {allCorrect && (
        <Box
          mt="4"
          p="4"
          bg="green.500"
          color="white"
          fontSize="lg"
          textAlign="center"
          borderRadius="md"
          animation="pulse 2s infinite"
        >
          🎉 All answers are correct! Great job! 🎉
        </Box>
      )}
    </Box>
  );
}
